import { ContextualInfoButton } from "@/components/buttons/ContextualInfoButton";
import { getHazardColor, getHazardIcon, isTpuLabellingEnabled } from "@/features/branding";
import { GEOCODE_QUALITY_PROVIDED, GeocodingHelpButton, HubGeocoderResult } from "@/features/geocoders";
import { AnalysisResultsTab } from "@/features/maps";
import { availableExposureHazards, getExposureStatusLabel, getReturnPeriodLabel, hazardToDisplayName, ReturnPeriod } from "@/features/pe_aldd";
import { RiskBand } from "@/features/risk_bands";
import { RiskBandsResponse, RiskBandsResponseSingleRcp } from "@/store/services/facades/types";
import { AbcMetadata } from "@/store/services/facades/types/abc";
import { HaloMetadata } from "@/store/services/facades/types/halo";
import { screeningFloodExposureReturnPeriod } from "@/tools/risk-bands/utils/shared";
import type { ExposureStatus } from "@/types/exposure";
import { Table, TableProps } from "antd";
import { startCase, toLower } from "lodash";
import styles from "./MultipleResultsTable.module.scss";
export interface GeocodeWithResult {
  geocode: HubGeocoderResult;
  result: RiskBandsResponse;
}
export interface GeocodeWithSingleRcp {
  geocode: HubGeocoderResult;
  result: RiskBandsResponseSingleRcp;
  metadata?: AbcMetadata | HaloMetadata;
}
interface AbcColumnProps {
  dataIndex: string;
  title: string;
}
interface MultipleResultsTableProps extends TableProps<any> {
  activeTab: AnalysisResultsTab;
  displayReturnPeriod?: ReturnPeriod;
  data: (GeocodeWithSingleRcp & {
    hk?: {
      tpu: string;
      subunit: string;
    };
  })[];
}
const buildAbcColumn = ({
  dataIndex,
  title
}: AbcColumnProps): any => {
  return {
    dataIndex,
    title,
    defaultSortOrder: "descend",
    sorter: {
      compare: (a: any, b: any) => a[dataIndex].rating > b[dataIndex].rating ? 1 : -1
    },
    sortDirections: ["ascend", "descend"],
    render: ({
      rating
    }: any) => <RiskBand rating={rating} size={24} />
  };
};
export const MultipleResultsTable = ({
  data,
  activeTab = "risk",
  displayReturnPeriod = screeningFloodExposureReturnPeriod,
  ...props
}: MultipleResultsTableProps) => {
  if (!data.length) {
    return <></>;
  }
  const columns: TableProps["columns"] = "ratings" in data[0].result ? data[0].result.ratings.map(({
    year
  }, index) => {
    return {
      title: String(year),
      sorter: {
        compare: (a: any, b: any) => a.ratings[index].rating > b.ratings[index].rating ? 1 : -1
      },
      sortDirections: ["descend", "ascend"],
      render: ({
        ratings
      }: any) => <RiskBand rating={ratings[index].rating} size={24} />
    };
  }) : [buildAbcColumn({
    dataIndex: "current_year",
    title: String(data[0].result.current_year.year)
  }), buildAbcColumn({
    dataIndex: "end_of_mortgage_term",
    title: String(data[0].result.end_of_mortgage_term.year)
  }), buildAbcColumn({
    dataIndex: "end_of_century",
    title: String(data[0].result.end_of_century.year)
  })];
  const tpuColumns: TableProps["columns"] = isTpuLabellingEnabled() ? [{
    dataIndex: "tpu",
    title: "TPU"
  }, {
    dataIndex: "subunit",
    title: "Subunit"
  }] : [];
  const exposureColumns: TableProps["columns"] = activeTab === "exposure" ? [{
    dataIndex: "return_period",
    title: <div className="flex items-center gap-1">
                Return Period
                <ContextualInfoButton id="returnPeriod" />
              </div>,
    className: "min-w-[145px]",
    render: () => getReturnPeriodLabel(displayReturnPeriod, false)
  }, ...availableExposureHazards.map(hazard => ({
    dataIndex: `${hazard}_exposure`,
    title: <div className="flex items-center gap-1">
                {getHazardIcon(hazard, {
        color: getHazardColor(hazard)
      })}
                {hazardToDisplayName(hazard)} Exposure
              </div>,
    className: hazard === "flood_surfacewater" ? "min-w-[265px]" : "min-w-[220px]",
    render: (_: any, row: any) => row?.exposures === undefined || row.exposures === null ? "No Data" : getExposureStatusLabel(row.exposures[hazard][displayReturnPeriod] as ExposureStatus)
  }))] : [];
  return <Table pagination={false} columns={[{
    dataIndex: "input",
    title: "Provided Address",
    width: "70%",
    className: "min-w-[300px]",
    render: name => name
  }, {
    dataIndex: "quality",
    title: <div className="flex items-center gap-1">
              Geocode Quality
              <GeocodingHelpButton className="!max-h-[14px] !max-w-[14px] *:!text-gray-400" />
            </div>,
    className: "min-w-[165px]",
    // default provided - quick batch list geocoding either successful, provided or omited from list
    render: name => startCase(toLower(name || GEOCODE_QUALITY_PROVIDED))
  }, ...tpuColumns, ...(activeTab === "exposure" ? exposureColumns : columns)]} dataSource={data.map(({
    geocode,
    result,
    hk = {}
  }, index) => ({
    input: geocode.input,
    quality: geocode.quality,
    key: index,
    ...result,
    ...hk
  }))} className={styles.table} {...props} data-sentry-element="Table" data-sentry-component="MultipleResultsTable" data-sentry-source-file="MultipleResultsTable.tsx" />;
};